import type { AreaItem } from './missionModels';

export type HierarchyArea = AreaItem & {
    children?: HierarchyArea[];
    hierarchyDepth?: number;
};

export const buildAreaTree = (areas: AreaItem[]): HierarchyArea[] => {
    const map = new Map<number, HierarchyArea>();

    areas.forEach((area) => {
        map.set(area.id, {
            ...area,
            children: [],
        });
    });

    const roots: HierarchyArea[] = [];

    areas.forEach((area) => {
        const current = map.get(area.id);
        if (!current) {
            return;
        }

        if (area.parentAreaId && map.has(area.parentAreaId)) {
            map.get(area.parentAreaId)?.children?.push(current);
        } else {
            roots.push(current);
        }
    });

    return roots;
};

export const buildAreaPathMap = (areas: AreaItem[]): Map<number, string> => {
    const tree = buildAreaTree(areas);
    const result = new Map<number, string>();

    const visit = (nodes: HierarchyArea[], ancestors: string[] = []) => {
        nodes.forEach((node) => {
            const path = [...ancestors, node.name].join(' --> ');
            result.set(node.id, path);
            if (node.children?.length) {
                visit(node.children, [...ancestors, node.name]);
            }
        });
    };

    visit(tree);
    return result;
};

export const collectAreaSubtree = (
    areas: AreaItem[],
    selectedAreaId: number,
    selectedAreaDetail?: AreaItem | null
): HierarchyArea[] => {
    const tree = buildAreaTree(areas);

    const findNode = (nodes: HierarchyArea[]): HierarchyArea | null => {
        for (const node of nodes) {
            if (node.id === selectedAreaId) {
                return node;
            }
            const match = node.children?.length ? findNode(node.children) : null;
            if (match) {
                return match;
            }
        }
        return null;
    };

    const selectedNode = findNode(tree);
    if (!selectedNode) {
        if (!selectedAreaDetail) {
            return [];
        }

        return [{ ...selectedAreaDetail, hierarchyDepth: 0, children: [] }];
    }

    const decorate = (node: HierarchyArea, depth: number): HierarchyArea => {
        const mergedNode = node.id === selectedAreaId && selectedAreaDetail
            ? {
                ...node,
                ...selectedAreaDetail,
                children: node.children,
            }
            : node;

        return {
            ...mergedNode,
            hierarchyDepth: depth,
            children: (mergedNode.children || []).map((child) => decorate(child, depth + 1)),
        };
    };

    const flatten = (node: HierarchyArea): HierarchyArea[] => [
        node,
        ...(node.children || []).flatMap((child) => flatten(child)),
    ];

    return flatten(decorate(selectedNode, 0));
};
